import useAuth from 'hooks/useAuth.ts';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'store';
import {
    APITimecardsSlice,
    useGetCurrentlyTrackedTimecardQuery,
    useGetTrackedTimecardByIdQuery,
} from 'store/api/timecards';
import { resetTimecardState } from 'store/reducers/timecard';
import { useTourMode } from 'tour/tourMode';
import { TimecardDetails, TimecardStatusType } from 'types/timecards';
import { useHasPermissions } from 'utils/permissionUtil';

export default function useTrackedTimecard() {
    const { user } = useAuth();
    const userId = user?.id;
    const activeTenantId = user?.activeTenantId;
    const dispatch = useDispatch();
    const hasReadTimecardsPermission = useHasPermissions(['READ.TIMECARDS.MY_OWN', 'READ.TIMECARDS.MATTER_ACCESS']);
    const storedTimecardId = useSelector((state) => state.timecard.trackedTimecardId);
    // Paused through the guided tour: its sample data holds no tracked timecard, and a miss on the stored one
    // would reset the real tracking below and tell the user's other tabs it finished.
    const touring = useTourMode();
    const { data: currentlyTrackedTimecard } = useGetCurrentlyTrackedTimecardQuery(
        { userId: userId!, tenantId: activeTenantId! },
        {
            skip: touring || !hasReadTimecardsPermission || !userId || !activeTenantId,
        }
    );
    const { data: storedTimecard, isError } = useGetTrackedTimecardByIdQuery(parseInt(storedTimecardId || '0'), {
        skip: touring || !hasReadTimecardsPermission || storedTimecardId === undefined || storedTimecardId === null,
    });

    // Remove timecard id from localstorage if the query fails and there's no currentlyTrackedTimecard
    useEffect(() => {
        if (isError && currentlyTrackedTimecard === null && userId && activeTenantId) {
            dispatch(resetTimecardState({ userId, tenantId: activeTenantId }));
        }
    }, [isError, currentlyTrackedTimecard, dispatch, userId, activeTenantId]);

    // Invalidate tracked timecard cache when tab becomes visible to sync with other tabs
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && hasReadTimecardsPermission && userId && activeTenantId) {
                // Invalidate the tracked timecard cache to trigger a refetch
                dispatch(APITimecardsSlice.util.invalidateTags(['TrackedTimecard']));
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [dispatch, hasReadTimecardsPermission, userId, activeTenantId]);

    return ((): TimecardDetails | undefined => {
        // A skipped query still returns the data it last had: without this the tour would show the real timer.
        if (touring) return undefined;
        if (currentlyTrackedTimecard) {
            return currentlyTrackedTimecard;
        }

        if (
            storedTimecard &&
            storedTimecardId &&
            storedTimecard.timecardStatusTypeCode === TimecardStatusType.DRAFT &&
            storedTimecard.employee.id === userId
        ) {
            return storedTimecard;
        }
    })();
}
